import * as React from 'react';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import * as Moment from 'moment-timezone';
import { Input, MaskedTextBox } from '@progress/kendo-react-inputs';
import { Editor, EditorTools, EditorUtils } from '@progress/kendo-react-editor';
import { fetchApi } from '../../services/api';
import StreetAddress, { StreetAddressViewModel } from '../StreetAddress';
import { ComboBox, DropDownList, ComboBoxFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { FilterDescriptor, filterBy } from '@progress/kendo-data-query';
import LoadingButton from '../LoadingButton';
const DocumentTitle = require('react-document-title');

const { Bold, Italic, Underline,
    AlignLeft, AlignRight, AlignCenter,
    Indent, Outdent,
    OrderedList, UnorderedList,
    Undo, Redo, Link, Unlink } = EditorTools;

type FacilityProps = RouteComponentProps<{
    facilityId: string
}>

type State = {
    isLoading: boolean;
    isDirty: boolean;
    Validation: boolean;

    filteredTimeZoneOptions: string[];
    facility: FacilityData|undefined;
}

type FacilityData = {
    FacilityId: number;
    Name: string;
    Phone: string;
    AcceptingApplications: boolean;
    LocalTimeZoneID: string;
    TaxRule: number;
    InvoiceTerms: string;
    InvoiceEmailTemplate: string;
    Address: StreetAddressViewModel;
    TimeZoneOptions: string[];
}

class Facility extends React.Component<FacilityProps, State> {

    private editor: React.RefObject<Editor>;

    constructor(props: FacilityProps) {
        super(props);

        this.state = {
            isLoading: true,
            isDirty: false,
            Validation: false,
            filteredTimeZoneOptions: [],
            facility: undefined,
        }

        this.editor = React.createRef();

        this.save = this.save.bind(this);
        this.filterChange = this.filterChange.bind(this);
        this.setHtml = this.setHtml.bind(this);
    }

    private filterChange(event: ComboBoxFilterChangeEvent) {
        if (event) {
            this.setState({
                filteredTimeZoneOptions: this.filterData(event.filter)
            });
        }
    }

    private filterData(filter: FilterDescriptor) {
        if (this.state.facility) {
            const data = this.state.facility.TimeZoneOptions.slice();
            return filterBy(data, filter);
        }
        return [];
    }

    public componentWillMount() {
        // This method runs when the component is first added to the page
        let facilityId = parseInt(this.props.match.params.facilityId) || 0;
        this.requestFacility(facilityId);
    }

    public componentWillReceiveProps(nextProps: FacilityProps) {
        // This method runs when incoming props (e.g., route params) change
        let facilityId = parseInt(nextProps.match.params.facilityId) || 0;

        if (this.state.facility && this.state.facility.FacilityId != facilityId) {
            this.requestFacility(facilityId);
        }
    }

    public render() {
        const taxRules = [{ ID: 0, Name: 'None' }, { ID: 1, Name: 'Ontario (13% HST)' }];
        return <React.Fragment>
            {this.state.facility &&
                <div className="k-form">
                    <DocumentTitle title={this.state.facility.Name || "New Facility"} />
                    <Prompt when={this.state.isDirty} message="Unsaved changes will be lost, are you sure?" />
                    {this.state.isLoading && <div className="k-loading-image"></div>}
                    <label className="k-form-field">
                        <span>Name<span style={{ color: '#dc3545' }}>*</span></span>
                        <Input
                            type="text"
                            placeholder="Name"
                            value={this.state.facility.Name}
                            onChange={(e) => this.updateFacilityState({ Name: e.target.value })}
                        />
                        {this.state.Validation && !this.state.facility.Name && <p className="error-msg">Facility Name is Required</p>}
                    </label>
                    <label className="k-form-field">
                        <span>Phone<span style={{ color: '#dc3545' }}>*</span></span>
                        <MaskedTextBox
                            mask="(000)000-0000"
                            name="Phone"
                            placeholder="(000)000-0000"
                            value={this.state.facility.Phone || ''}
                            onChange={(e) => this.updateFacilityState({ Phone: e.target.value.replace(/_/g, "") })}
                        />
                        {this.state.Validation && (!this.state.facility.Phone || this.state.facility.Phone.length < 13) && <p className="error-msg">Phone Number is Required</p>}
                    </label>
                    <label className="k-form-field">
                        <span>Time Zone<span style={{ color: '#dc3545' }}>*</span></span>
                        <ComboBox
                            data={this.state.filteredTimeZoneOptions}
                            value={this.state.facility.LocalTimeZoneID}
                            filterable={true}
                            onFilterChange={this.filterChange}
                            onChange={(e) => {
                                this.updateFacilityState({ LocalTimeZoneID: e.target.value });
                            }}
                            style={{ width: '100%' }}
                        />
                        {this.state.Validation && !this.state.facility.LocalTimeZoneID && <p className="error-msg">Time Zone is Required</p>}
                    </label>
                    <label className="k-form-field">
                        <span>Tax Rules</span>
                        <DropDownList
                            data={taxRules}
                            value={taxRules.find(x => x.ID === this.state.facility.TaxRule)}
                            onChange={(e) => this.updateFacilityState({ TaxRule: e.target.value.ID })}
                            textField="Name"
                        />
                        {this.state.Validation && !this.state.facility.LocalTimeZoneID && <p className="error-msg">Time Zone is Required</p>}
                    </label>
                    <label className="k-form-field">
                        <span>Invoice Terms</span>
                        <textarea
                            className="k-textarea"
                            style={{ width: "100%" }}
                            value={this.state.facility.InvoiceTerms}
                            onChange={(e) => this.updateFacilityState({ InvoiceTerms: e.target.value })}
                            placeholder="This will be displayed at the bottom of all invoices..."
                        />
                    </label>

                    <label className="k-form-field" style={{ marginBottom: '.5rem' }}>
                        <span>Invoice Email Template <span className="k-field-info">(You can use {'<ACCOUNT>'} and {'<AMOUNT>'} variables in the email template)</span></span>
                    </label>
                    <Editor
                        className="bg-light"
                        ref={this.editor}
                        tools={[
                            [ Bold, Italic, Underline ],
                            [ Undo, Redo ],
                            [ Link, Unlink ],
                            [ AlignLeft, AlignCenter, AlignRight ],
                            [ OrderedList, UnorderedList, Indent, Outdent ]
                        ]}
                        onExecute={(event) => {
                            // Wish this would skip the first time..
                            this.setState({ isDirty: true });
                        }}
                        onMount={this.setHtml}
                        contentStyle={{ height: 320 }}
                    />
                    <br />
                    
                    <StreetAddress
                        address={this.state.facility.Address}
                        onChange={(newAddress) => this.updateFacilityState({ Address: newAddress })}
                        title="Facility Address"
                    />
                    <br/>
                    <br/>
                    <label className="k-form-field">
                        <input
                            type="checkbox"
                            id="accepting-applications"
                            className="k-checkbox"
                            checked={this.state.facility.AcceptingApplications}
                            onChange={(e) => this.updateFacilityState({ AcceptingApplications: e.target.checked })} />
                        <label className="k-checkbox-label" htmlFor="accepting-applications">Accepting Applications</label>
                    </label>
                    <div className="text-right">
                        <LoadingButton 
                            onClick={this.save}
                            disabled={!this.state.isDirty}
                            isLoading={this.state.isLoading}
                            isLoadingText="Saving..."
                            className="btn btn-primary"
                        >
                            {this.state.facility.FacilityId === 0 ? 'Create' : 'Save'}
                        </LoadingButton>
                    </div>
                </div>
            }
        </React.Fragment>;
    }

    private updateFacilityState(value: Partial<FacilityData>) {
        const facility = Object.assign({}, this.state.facility, value);
        this.setState({ facility, isDirty: true });
    }

    private requestFacility(facilityId: number) {
        this.setState({ isLoading: true });
        fetchApi(`api/Facilities/Get/${facilityId}`)
            .then((facility: FacilityData) => {
                if (facilityId === 0) {
                    facility.LocalTimeZoneID = Moment.tz.guess();
                }
                this.setState({ isLoading: false, isDirty: false, facility, filteredTimeZoneOptions: facility.TimeZoneOptions.slice() });
            });
    }

    private setHtml(event: any) {
        setTimeout(() => {
            if (this.state.facility.InvoiceEmailTemplate) {
                const view = this.editor.current.view;
                EditorUtils.setHtml(view, this.state.facility.InvoiceEmailTemplate);
            }
        })
    }

    private save() {
        const view = this.editor.current.view;
        const html = EditorUtils.getHtml(view.state);
        this.updateFacilityState({ InvoiceEmailTemplate: html === '<p></p>' ? '' : html });
        
        this.setState({ Validation: true }, () => {
            if (this.state.facility && document.getElementsByClassName("error-msg").length === 0) {
                this.setState({ isLoading: true });
                if (this.state.facility.FacilityId > 0) {
                    fetchApi(`api/Facilities/Save/${this.state.facility.FacilityId}`, this.state.facility, 'POST')
                        .then(() => {
                            this.setState({ isLoading: false, isDirty: false });
                        });
                } else {
                    fetchApi(`api/Facilities/Create/`, this.state.facility, 'POST')
                        .then((response: { FacilityID: number }) => {
                            this.updateFacilityState({ FacilityId: response.FacilityID });
                            this.setState({ isLoading: false, isDirty: false });

                            this.props.history.push("/facilities");
                        });
                }
            }
        });
    }
}

export default Facility;
